import React, { ReactNode, useEffect, useRef } from "react";
import styles from "./SideModal.module.scss";
import classNames from "classnames";
import { ArrowDownIcon, CloseWhiteIcon } from "Assets";
import { lockBodyScroll } from "Shared/Utils/scrollLock";

type Props = {
  /** Use null as initial state to avoid animation of first render */
  isOpen: boolean | null;
  accessibleLabel?: string;
  id?: string;
  onClose: () => void;
  className?: string;
  headerClassName?: string;
  headerChildren?: ReactNode;
  actionButtons?: ReactNode;
  title?: ReactNode;
  extraConditionToOutsideClick?: boolean;
  children: ReactNode;
  fixed?: boolean;
  animationType?: "horizontal" | "vertical";
};

export const SideModal = ({
  isOpen,
  accessibleLabel,
  id,
  onClose,
  className,
  headerClassName,
  children,
  headerChildren,
  title,
  actionButtons,
  fixed,
  extraConditionToOutsideClick = false,
  animationType = "horizontal",
}: Props) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!accessibleLabel || !isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    modalRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [accessibleLabel, isOpen, onClose]);

  // Stop the page behind a fixed modal from scrolling.
  useEffect(
    () => (fixed && isOpen ? lockBodyScroll() : undefined),
    [fixed, isOpen],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (accessibleLabel ? isOpen === true : isOpen !== null) &&
        !(
          id &&
          event.target instanceof Element &&
          event.target
            .closest("[aria-controls]")
            ?.getAttribute("aria-controls") === id
        ) &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        extraConditionToOutsideClick === false
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    onClose,
    isOpen,
    extraConditionToOutsideClick,
    accessibleLabel,
    id,
  ]);

  return (
    <>
      {fixed && isOpen && <div className={styles.overlay} />}
      <div
        ref={modalRef}
        id={id}
        role={accessibleLabel ? "dialog" : undefined}
        aria-label={accessibleLabel}
        hidden={accessibleLabel ? !isOpen : undefined}
        tabIndex={accessibleLabel ? -1 : undefined}
        className={classNames(
          styles.sideModal,
          className,
          isOpen === null
            ? null
            : isOpen
              ? animationType === "horizontal"
                ? styles.sideModal_open
                : styles.sideModal_openY
              : animationType === "horizontal"
                ? styles.sideModal_closed
                : styles.sideModal_closedY,
        )}
      >
        <div className={classNames(styles.sideModal__header, headerClassName)}>
          <div
            className={styles.sideModal__headerTitle}
            onClick={accessibleLabel ? undefined : onClose}
          >
            <p>{title}</p>
            <div className={styles.sideModal__actions}>
              {!!actionButtons && actionButtons}
              {accessibleLabel ? (
                <button
                  type="button"
                  className={styles.accessibleClose}
                  onClick={onClose}
                  aria-label={`Close ${accessibleLabel.toLowerCase()}`}
                >
                  <CloseWhiteIcon width={25} height={25} aria-hidden="true" />
                </button>
              ) : animationType === "horizontal" ? (
                <CloseWhiteIcon width={25} height={25} />
              ) : (
                <ArrowDownIcon
                  className={styles.sideModal__closeBtnDown}
                  width={25}
                  height={25}
                />
              )}
            </div>
          </div>
          {!!headerChildren && headerChildren}
        </div>
        <div className={styles.sideModal__content}>{children}</div>
      </div>
    </>
  );
};
